import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { BarChart3, TrendingUp, Hourglass, Users, PieChart, RefreshCw, AlertTriangle, ChevronRight, Send, Settings, Clock, Mail, Zap, CheckCircle2, Download, Share2 } from "lucide-react";
import { API_BASE, formatApiError, useAuth } from "../components/AuthContext";
import TeammateDrillThrough from "../components/TeammateDrillThrough";
import MarketingRoiCard from "../components/MarketingRoiCard";

const STAGE_META = {
  new:                   { label: "New",       accent: "var(--ink-3)" },
  information_requested: { label: "Info req'd", accent: "var(--accent)" },
  documents_received:    { label: "Docs in",   accent: "var(--moss)" },
  in_preparation:        { label: "Preparing", accent: "var(--ink)" },
  awaiting_review:       { label: "Review",    accent: "var(--ink)" },
  lodged:                { label: "Lodged",    accent: "var(--moss)" },
  deferred:              { label: "Deferred",  accent: "#B91C1C" },
  lost:                  { label: "Lost",      accent: "var(--ink-3)" },
};

const SOURCE_LABEL = {
  email: "Email", whatsapp: "WhatsApp", phone: "Phone", referral: "Referral",
  website_form: "Website form", viber: "Viber", walk_in: "Walk-in", manual: "Manual",
};

const formatMoney = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function Reports() {
  const { state } = useAuth();
  const isAdmin = ["owner", "manager", "admin"].includes(state?.user?.role);
  const [days, setDays] = useState(90);
  const [funnel, setFunnel] = useState(null);
  const [deferred, setDeferred] = useState(null);
  const [workload, setWorkload] = useState(null);
  const [sources, setSources] = useState(null);
  const [velocity, setVelocity] = useState(null);
  const [interested, setInterested] = useState(null);
  const [staff, setStaff] = useState(null);
  const [trend, setTrend] = useState(null);
  const [realisation, setRealisation] = useState(null);
  const [realisationTrend, setRealisationTrend] = useState(null);   // v68.12 — realisation trend sparkline
  const [loading, setLoading] = useState(true);
  const [openSettings, setOpenSettings] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [f, d, w, s, v, i, sw, pt, real, rt] = await Promise.all([
        axios.get(`${API_BASE}/reports/funnel?days=${days}`),
        axios.get(`${API_BASE}/reports/deferred`),
        axios.get(`${API_BASE}/reports/workload`),
        axios.get(`${API_BASE}/reports/source-attribution?days=${days}`),
        axios.get(`${API_BASE}/reports/velocity?days=${days}`),
        axios.get(`${API_BASE}/reports/interested-services?days=${days}`),
        axios.get(`${API_BASE}/reports/staff-workload`),
        axios.get(`${API_BASE}/reports/pipeline-trend?weeks=12`),
        axios.get(`${API_BASE}/reports/realisation?days=${days}`),
        axios.get(`${API_BASE}/reports/realisation-trend?weeks=12`),
      ]);
      setFunnel(f.data); setDeferred(d.data); setWorkload(w.data); setSources(s.data); setVelocity(v.data); setInterested(i.data); setStaff(sw.data); setTrend(pt.data);
      setRealisation(real.data); setRealisationTrend(rt.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [days]);

  useEffect(() => { reload(); }, [reload]);

  // Allow children (MarketingRoiCard) to trigger a source-attribution refresh
  useEffect(() => {
    const handler = () => { reload(); };
    window.addEventListener("reports:reload", handler);
    return () => window.removeEventListener("reports:reload", handler);
  }, [reload]);

  const sendPulseNow = async () => {
    if (!window.confirm("Send the Practice Pulse digest to firm owners now?")) return;
    try {
      const r = await axios.post(`${API_BASE}/reports/practice-pulse/run-now`);
      toast.success(`Digest sent · ${r.data.sent} delivered · ${r.data.blocked} blocked · ${r.data.skipped} skipped`);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="px-10 py-12 max-w-[1500px]" data-testid="reports-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="overline mb-2">Phase 1 · Analytics</div>
          <h1 className="font-heading text-4xl" style={{ fontWeight: 500 }}>
            Pipeline <em className="italic" style={{ color: "var(--accent)" }}>reports</em>
          </h1>
          <p className="text-[var(--ink-2)] mt-2 max-w-2xl">
            Where leads are stuck, what's overdue, who's loaded up, and where new business is coming from. Live from the lead pipeline — no spreadsheets.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))} className="bg-white border border-[var(--border)] rounded-md px-3 py-2 text-[13px]" data-testid="reports-days-select">
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={180}>Last 180 days</option>
            <option value={365}>Last year</option>
          </select>
          <button onClick={() => setOpenSettings(true)} className="btn-secondary" data-testid="reports-pulse-settings">
            <Settings size={13}/> Pulse settings
          </button>
          <button onClick={sendPulseNow} className="btn-secondary" data-testid="reports-send-pulse">
            <Send size={13}/> Email pulse
          </button>
          <button onClick={reload} disabled={loading} className="btn-secondary" data-testid="reports-refresh">
            <RefreshCw size={13} className={loading ? "animate-spin" : ""}/> Refresh
          </button>
          <a
            href={`${API_BASE}/reports/all.pdf?days=${days}`}
            target="_blank"
            rel="noreferrer"
            className="btn-secondary"
            data-testid="reports-pdf"
          >
            <Download size={13}/> PDF
          </a>
        </div>
      </div>

      {loading && !funnel ? (
        <div className="text-[var(--ink-3)] text-sm">Loading reports…</div>
      ) : (
        <div className="space-y-6">
          {/* Top KPI strip */}
          <KpiStrip funnel={funnel} deferred={deferred} workload={workload} sources={sources} />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <FunnelCard funnel={funnel} days={days} />
            <VelocityCard velocity={velocity} days={days} />
            <DeferredCard deferred={deferred} />
            <WorkloadCard workload={workload} />
            <SourceAttributionCard sources={sources} days={days} />
            <InterestedServicesCard interested={interested} days={days} />
            <StaffWorkloadCard staff={staff} />
            <RealisationCard data={realisation} trend={realisationTrend} days={days} />
            <PipelineTrendCard trend={trend} />
            <MarketingRoiCard days={days} sources={sources} isAdmin={isAdmin} />
            <JobProfitabilityCard />
            <ReferralRevenueCard days={days} />
          </div>
        </div>
      )}
      {openSettings && <PulseSettingsModal onClose={() => setOpenSettings(false)} />}
    </div>
  );
}

function KpiStrip({ funnel, deferred, workload, sources }) {
  if (!funnel) return null;
  const totalLodgedRevenue = (sources?.sources || []).reduce((s, x) => s + (x.revenue_lodged || 0), 0);
  const totalActive = (workload?.workload || []).reduce((s, x) => s + x.count, 0);
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="reports-kpi-strip">
      <Kpi label="Total leads" value={funnel.total_leads} icon={<BarChart3 size={14}/>} testid="kpi-total-leads"/>
      <Kpi label="Pipeline value" value={formatMoney(funnel.pipeline_value || 0)} sub={`${funnel.pipeline_count || 0} active`} icon={<TrendingUp size={14}/>} testid="kpi-pipeline-value"/>
      <Kpi label="Conversion" value={`${funnel.conversion_rate}%`} sub={`${funnel.lodged_count} lodged`} icon={<TrendingUp size={14}/>} testid="kpi-conversion"/>
      <Kpi label="Active workload" value={totalActive} sub={`${(workload?.workload || []).length} preparers`} icon={<Users size={14}/>} testid="kpi-workload"/>
      <Kpi label="Lodged revenue" value={formatMoney(totalLodgedRevenue)} icon={<PieChart size={14}/>} testid="kpi-revenue"/>
    </div>
  );
}

function Kpi({ label, value, sub, icon, testid }) {
  return (
    <div className="card p-4" data-testid={testid}>
      <div className="flex items-center gap-1.5 overline text-[10px] mb-2">{icon}{label}</div>
      <div className="font-mono text-[24px] text-[var(--ink)]">{value}</div>
      {sub && <div className="text-[10px] text-[var(--ink-3)] mt-1">{sub}</div>}
    </div>
  );
}

function FunnelCard({ funnel, days }) {
  if (!funnel) return null;
  const maxCount = Math.max(1, ...funnel.stages.map((s) => s.count));
  return (
    <div className="card p-5" data-testid="funnel-card">
      <div className="flex items-center justify-between mb-4">
        <div className="overline inline-flex items-center gap-1"><BarChart3 size={12}/> Pipeline funnel · {days}d</div>
        <div className="text-[11px] text-[var(--ink-3)]">
          {funnel.total_leads} leads · {funnel.conversion_rate}% converted
        </div>
      </div>
      <div className="space-y-2" data-testid="funnel-stages">
        {funnel.stages.map((s) => {
          const pct = (s.count / maxCount) * 100;
          const meta = STAGE_META[s.status] || { label: s.status, accent: "var(--ink-3)" };
          return (
            <Link key={s.status} to={`/leads?status=${s.status}&days=${days}`} className="flex items-center gap-3 hover:opacity-80 transition-opacity cursor-pointer" data-testid={`funnel-stage-${s.status}`}>
              <div className="w-24 text-[12px] text-[var(--ink-2)] truncate flex-shrink-0">{meta.label}</div>
              <div className="flex-1 h-6 bg-[var(--subtle)] rounded relative overflow-hidden">
                <div className="h-full transition-all" style={{ width: `${pct}%`, background: meta.accent, opacity: 0.85 }}/>
                {s.count > 0 && (
                  <div className="absolute inset-0 flex items-center px-2 text-[11px] font-mono" style={{ color: pct > 35 ? "var(--paper)" : "var(--ink)" }}>
                    {s.count}
                  </div>
                )}
              </div>
              <div className="w-20 text-[11px] text-[var(--ink-3)] text-right font-mono flex-shrink-0">{formatMoney(s.total_fee)}</div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function VelocityCard({ velocity, days }) {
  if (!velocity) return null;
  const lodgedCount = velocity.lodged_count || 0;
  const breached = (velocity.stage_sla || []).filter((s) => s.breached);
  return (
    <div className="card p-5" data-testid="velocity-card">
      <div className="flex items-center justify-between mb-4">
        <div className="overline inline-flex items-center gap-1"><Zap size={12}/> Lodgement velocity · {days}d</div>
        <div className="text-[11px] text-[var(--ink-3)]">{lodgedCount} lodged</div>
      </div>
      {lodgedCount === 0 ? (
        <div className="text-[12px] text-[var(--ink-3)] italic py-6 text-center">
          No lodgements in this window — once leads start completing, you'll see cycle times here.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2 mb-4" data-testid="velocity-stats">
            <Stat label="Avg" value={`${velocity.avg_days_to_lodgement}d`} accent="var(--ink)"/>
            <Stat label="Median" value={`${velocity.median_days_to_lodgement}d`} accent="var(--ink-2)"/>
            <Stat label="Range" value={`${velocity.fastest_days}–${velocity.slowest_days}d`} accent="var(--ink-3)" small/>
          </div>
          {breached.length > 0 ? (
            <div className="rounded-md border-l-3 border-l-[#B91C1C] bg-[#FEE2E2] p-3" data-testid="velocity-sla-breaches">
              <div className="text-[10px] uppercase tracking-wider text-[#B91C1C] font-semibold mb-1.5 inline-flex items-center gap-1">
                <AlertTriangle size={11}/> SLA breaches
              </div>
              <div className="space-y-1">
                {breached.map((s) => (
                  <div key={s.stage} className="flex items-center justify-between text-[12px]" data-testid={`sla-breach-${s.stage}`}>
                    <span className="text-[var(--ink)]">{s.stage.replace(/_/g, " ")}</span>
                    <span className="font-mono text-[var(--ink-2)]">
                      avg {s.avg_hours}h <span className="text-[var(--ink-3)]">(target {s.target_hours}h)</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-[11px] text-[var(--moss)] inline-flex items-center gap-1">
              <CheckCircle2 size={11}/> All stage SLAs met
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Stat({ label, value, accent, small }) {
  return (
    <div className="border border-[var(--border)] rounded-md p-2.5">
      <div className="text-[9px] uppercase tracking-wider text-[var(--ink-3)] mb-1">{label}</div>
      <div className={`font-mono ${small ? "text-[14px]" : "text-[18px]"}`} style={{ color: accent }}>{value}</div>
    </div>
  );
}

function DeferredCard({ deferred }) {
  if (!deferred) return null;
  return (
    <div className="card p-5" data-testid="deferred-card">
      <div className="flex items-center justify-between mb-4">
        <div className="overline inline-flex items-center gap-1"><Hourglass size={12}/> Deferred leads</div>
        <div className="text-[11px] text-[var(--ink-3)]">
          {deferred.count} total · {deferred.overdue_count > 0 && (
            <span className="text-[#B91C1C] font-semibold inline-flex items-center gap-1" data-testid="deferred-overdue-count">
              <AlertTriangle size={10}/> {deferred.overdue_count} overdue
            </span>
          )}
        </div>
      </div>
      {deferred.leads.length === 0 ? (
        <div className="text-[12px] text-[var(--ink-3)] italic py-6 text-center">
          Nothing deferred — clean queue.
        </div>
      ) : (
        <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="deferred-leads-list">
          {deferred.leads.map((l) => {
            const overdue = l.days_until_follow_up !== null && l.days_until_follow_up < 0;
            const due = l.days_until_follow_up !== null && l.days_until_follow_up >= 0 && l.days_until_follow_up <= 7;
            return (
              <Link key={l.lead_id} to={`/leads?status=deferred`} className="block border border-[var(--border)] rounded-md px-3 py-2 hover:border-[var(--ink)] transition-colors" data-testid={`deferred-row-${l.lead_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] text-[var(--ink)] truncate">{l.name}</div>
                    <div className="text-[10px] text-[var(--ink-3)] truncate">
                      {l.deferral_reason || <i>no reason captured</i>}
                    </div>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    {l.days_until_follow_up !== null ? (
                      <div className={`text-[11px] font-mono ${overdue ? "text-[#B91C1C]" : due ? "text-[var(--accent)]" : "text-[var(--ink-2)]"}`}>
                        {overdue ? `${Math.abs(l.days_until_follow_up)}d overdue` : `${l.days_until_follow_up}d`}
                      </div>
                    ) : (
                      <div className="text-[10px] text-[var(--ink-3)] italic">no date</div>
                    )}
                    <div className="text-[9px] text-[var(--ink-3)] font-mono">{l.follow_up_at?.slice(0, 10) || "—"}</div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

function WorkloadCard({ workload }) {
  if (!workload) return null;
  const rows = workload.workload || [];
  const maxCount = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="card p-5" data-testid="workload-card">
      <div className="flex items-center justify-between mb-4">
        <div className="overline inline-flex items-center gap-1"><Users size={12}/> Preparer workload</div>
        <div className="text-[11px] text-[var(--ink-3)]">Active leads only</div>
      </div>
      {rows.length === 0 ? (
        <div className="text-[12px] text-[var(--ink-3)] italic py-6 text-center">No active leads.</div>
      ) : (
        <div className="space-y-2" data-testid="workload-rows">
          {rows.map((r) => {
            const pct = (r.count / maxCount) * 100;
            const filterId = r.assignee_id || "__unassigned__";
            return (
              <Link key={r.assignee_id || "unassigned"} to={`/leads?assignee_id=${encodeURIComponent(filterId)}`} className="block border border-[var(--border)] rounded-md px-3 py-2 hover:border-[var(--ink)] transition-colors" data-testid={`workload-row-${r.assignee_id || "unassigned"}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-7 h-7 rounded-full bg-[var(--subtle)] flex items-center justify-center text-[11px] font-mono text-[var(--ink-2)]">
                      {(r.name || "?").slice(0, 2).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="text-[13px] text-[var(--ink)] truncate">{r.name}</div>
                      {r.email && <div className="text-[10px] text-[var(--ink-3)] truncate">{r.email}</div>}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="font-mono text-[16px] text-[var(--ink)]">{r.count}</div>
                    <div className="text-[10px] text-[var(--ink-3)] font-mono">{formatMoney(r.total_fee)}</div>
                  </div>
                </div>
                <div className="h-1.5 bg-[var(--subtle)] rounded overflow-hidden">
                  <div className="h-full" style={{ width: `${pct}%`, background: "var(--accent)" }}/>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {Object.entries(r.by_status || {}).map(([s, n]) => (
                    <span key={s} className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-[var(--border)]" style={{ color: (STAGE_META[s]?.accent) || "var(--ink-3)" }}>
                      {STAGE_META[s]?.label || s} · {n}
                    </span>
                  ))}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SourceAttributionCard({ sources, days }) {
  if (!sources) return null;
  const rows = sources.sources || [];
  const maxCount = Math.max(1, ...rows.map((r) => r.count));
  // Highlight top 3 sources by combined value (won_value first, falling back to pipeline_value)
  const topSources = [...rows]
    .map((r) => ({ ...r, _score: (r.won_value || 0) + (r.pipeline_value || 0) }))
    .sort((a, b) => b._score - a._score)
    .filter((r) => r._score > 0)
    .slice(0, 3);
  const fmtMoneyShort = (v) => {
    if (!v) return "$0";
    return v >= 1000 ? `$${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : `$${v.toFixed(0)}`;
  };
  return (
    <div className="card p-5" data-testid="source-attribution-card">
      <div className="flex items-center justify-between mb-4">
        <div className="overline inline-flex items-center gap-1"><PieChart size={12}/> Lead source attribution · {days}d</div>
        <div className="flex items-center gap-3">
          <a
            href={`${API_BASE}/reports/source-attribution.pdf?days=${days}`}
            target="_blank"
            rel="noreferrer"
            className="text-[11px] text-[var(--ink-3)] hover:text-[var(--ink)] inline-flex items-center gap-1 underline"
            data-testid="source-attribution-pdf"
          >
            Download PDF
          </a>
          <div className="text-[11px] text-[var(--ink-3)]">Where new business comes from</div>
        </div>
      </div>
      {topSources.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4" data-testid="source-tiles">
          {topSources.map((r) => {
            const headline = r.pipeline_value > 0 ? r.pipeline_value : r.won_value;
            const headlineLabel = r.pipeline_value > 0 ? "in pipeline" : "won";
            return (
            <Link
              key={r.source}
              to={`/leads?source=${encodeURIComponent(r.source)}&days=${days}`}
              data-testid={`source-tile-${r.source}`}
              className="rounded-md border p-3 hover:bg-[var(--subtle)] transition"
              style={{ borderColor: "var(--border)" }}
            >
              <div className="text-[11px] uppercase tracking-wide text-[var(--ink-3)] truncate">
                {SOURCE_LABEL[r.source] || r.source}
              </div>
              <div className="text-[18px] font-medium text-[var(--ink)] font-mono mt-0.5">
                {fmtMoneyShort(headline)} <span className="text-[10px] font-normal text-[var(--ink-3)] uppercase tracking-wide">{headlineLabel}</span>
              </div>
              <div className="text-[10px] text-[var(--ink-3)] mt-0.5">
                {r.count} lead{r.count === 1 ? "" : "s"} ·{" "}
                <span style={{ color: r.conversion_rate >= 50 ? "var(--moss)" : r.conversion_rate >= 20 ? "var(--ink-2)" : "var(--ink-3)" }}>
                  {r.conversion_rate}% conv
                </span>
                {r.pipeline_value > 0 && r.won_value > 0 && <> · won {fmtMoneyShort(r.won_value)}</>}
              </div>
            </Link>
            );
          })}
        </div>
      )}
      {rows.length === 0 ? (
        <div className="text-[12px] text-[var(--ink-3)] italic py-6 text-center">No leads in this window.</div>
      ) : (
        <div className="space-y-2" data-testid="source-rows">
          <div className="grid grid-cols-12 text-[9px] uppercase tracking-wider text-[var(--ink-3)] px-1 pb-1 border-b border-[var(--border)]">
            <div className="col-span-3">Source</div>
            <div className="col-span-3">Volume</div>
            <div className="col-span-2 text-right">Conv %</div>
            <div className="col-span-2 text-right">Pipeline</div>
            <div className="col-span-2 text-right">Revenue</div>
          </div>
          {rows.map((r) => {
            const pct = (r.count / maxCount) * 100;
            return (
              <Link key={r.source} to={`/leads?source=${encodeURIComponent(r.source)}&days=${days}`} className="grid grid-cols-12 items-center gap-2 text-[12px] py-1.5 hover:bg-[var(--subtle)] rounded transition-colors" data-testid={`source-row-${r.source}`}>
                <div className="col-span-3 text-[var(--ink-2)] truncate">{SOURCE_LABEL[r.source] || r.source}</div>
                <div className="col-span-3 flex items-center gap-2">
                  <div className="flex-1 h-3 bg-[var(--subtle)] rounded overflow-hidden">
                    <div className="h-full" style={{ width: `${pct}%`, background: "var(--ink)" }}/>
                  </div>
                  <span className="font-mono text-[11px] text-[var(--ink-3)] w-8 text-right">{r.count}</span>
                </div>
                <div className="col-span-2 text-right font-mono text-[12px]" style={{ color: r.conversion_rate >= 50 ? "var(--moss)" : r.conversion_rate >= 20 ? "var(--ink)" : "var(--ink-3)" }}>
                  {r.conversion_rate}%
                </div>
                <div className="col-span-2 text-right font-mono text-[12px] text-[var(--ink-2)]">{fmtMoneyShort(r.pipeline_value)}</div>
                <div className="col-span-2 text-right font-mono text-[12px]">{formatMoney(r.revenue_lodged)}</div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

const DAYS = [
  ["mon", "Mon"], ["tue", "Tue"], ["wed", "Wed"], ["thu", "Thu"],
  ["fri", "Fri"], ["sat", "Sat"], ["sun", "Sun"],
];

const COMMON_TZ = [
  "UTC", "Australia/Sydney", "Australia/Melbourne", "Australia/Perth",
  "America/New_York", "America/Los_Angeles", "Europe/London", "Asia/Singapore",
];

function PulseSettingsModal({ onClose }) {
  const [settings, setSettings] = useState(null);
  const [optOut, setOptOut] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, o] = await Promise.all([
        axios.get(`${API_BASE}/reports/practice-pulse/settings`),
        axios.get(`${API_BASE}/auth/me/pulse-opt-out`),
      ]);
      setSettings(s.data);
      setOptOut(!!o.data.opted_out);
    } catch (err) { toast.error(formatApiError(err)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveSchedule = async () => {
    setBusy(true);
    try {
      const r = await axios.put(`${API_BASE}/reports/practice-pulse/settings`, settings);
      setSettings(r.data);
      toast.success("Pulse schedule saved");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const toggleOptOut = async (next) => {
    setBusy(true);
    try {
      await axios.put(`${API_BASE}/auth/me/pulse-opt-out`, { opted_out: next });
      setOptOut(next);
      toast.success(next ? "You'll no longer receive the pulse" : "You're back on the pulse list");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-[var(--ink)]/40 flex items-center justify-center p-6" onClick={onClose} data-testid="pulse-settings-modal">
      <div className="bg-[var(--paper)] rounded-lg w-full max-w-xl border border-[var(--border)]" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-[var(--border)] flex items-center justify-between">
          <h2 className="font-heading text-xl inline-flex items-center gap-2" style={{ fontWeight: 500 }}>
            <Clock size={18}/> Practice pulse settings
          </h2>
          <button onClick={onClose} className="text-[var(--ink-3)] hover:text-[var(--ink)]" data-testid="pulse-close">✕</button>
        </div>
        {!settings ? (
          <div className="p-6 text-[12px] text-[var(--ink-3)]">Loading…</div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Per-user opt-out */}
            <div>
              <div className="overline mb-2 text-[10px] inline-flex items-center gap-1"><Mail size={11}/> My subscription</div>
              <label className="flex items-start gap-3 cursor-pointer border border-[var(--border)] rounded-md p-3">
                <input type="checkbox" checked={!optOut} onChange={(e) => toggleOptOut(!e.target.checked)} disabled={busy} className="mt-0.5 accent-[var(--accent)]" data-testid="pulse-opt-in-toggle"/>
                <div className="flex-1">
                  <div className="text-[13px] text-[var(--ink)]">Email me the weekly Practice Pulse</div>
                  <div className="text-[10px] text-[var(--ink-3)] mt-0.5">Funnel, deferred, workload & source attribution — only owners and managers can subscribe.</div>
                </div>
              </label>
            </div>

            {/* Per-org schedule */}
            <div>
              <div className="overline mb-2 text-[10px] inline-flex items-center gap-1"><Clock size={11}/> Org-wide schedule</div>
              <div className="border border-[var(--border)] rounded-md p-3 space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={settings.enabled} onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })} className="accent-[var(--accent)]" data-testid="pulse-enabled-toggle"/>
                  <span className="text-[13px] text-[var(--ink)]">Pulse digest enabled</span>
                </label>
                <div>
                  <div className="text-[10px] text-[var(--ink-3)] mb-1.5 uppercase tracking-wider">Day</div>
                  <div className="flex gap-1 flex-wrap" data-testid="pulse-day-picker">
                    {DAYS.map(([key, label]) => (
                      <button key={key} onClick={() => setSettings({ ...settings, day_of_week: key })} disabled={!settings.enabled}
                        className={`text-[11px] px-3 py-1.5 rounded-full transition-colors ${settings.day_of_week === key ? "bg-[var(--ink)] text-[var(--paper)]" : "text-[var(--ink-3)] border border-[var(--border)] hover:border-[var(--ink)]"} disabled:opacity-50`}
                        data-testid={`pulse-day-${key}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <label className="block">
                    <div className="text-[10px] text-[var(--ink-3)] mb-1 uppercase tracking-wider">Hour</div>
                    <input type="number" min={0} max={23} value={settings.hour} disabled={!settings.enabled}
                      onChange={(e) => setSettings({ ...settings, hour: parseInt(e.target.value, 10) || 0 })}
                      className="w-full bg-white border border-[var(--border)] rounded-md px-3 py-2 text-[13px] disabled:opacity-50"
                      data-testid="pulse-hour-input"/>
                  </label>
                  <label className="block">
                    <div className="text-[10px] text-[var(--ink-3)] mb-1 uppercase tracking-wider">Minute</div>
                    <input type="number" min={0} max={59} value={settings.minute} disabled={!settings.enabled}
                      onChange={(e) => setSettings({ ...settings, minute: parseInt(e.target.value, 10) || 0 })}
                      className="w-full bg-white border border-[var(--border)] rounded-md px-3 py-2 text-[13px] disabled:opacity-50"
                      data-testid="pulse-minute-input"/>
                  </label>
                  <label className="block col-span-1">
                    <div className="text-[10px] text-[var(--ink-3)] mb-1 uppercase tracking-wider">Timezone</div>
                    <select value={settings.timezone} disabled={!settings.enabled}
                      onChange={(e) => setSettings({ ...settings, timezone: e.target.value })}
                      className="w-full bg-white border border-[var(--border)] rounded-md px-3 py-2 text-[12px] disabled:opacity-50"
                      data-testid="pulse-timezone-input">
                      {COMMON_TZ.includes(settings.timezone) ? null : <option value={settings.timezone}>{settings.timezone}</option>}
                      {COMMON_TZ.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                    </select>
                  </label>
                </div>
                <div className="text-[10px] text-[var(--ink-3)]">
                  Next send: every <b>{DAYS.find(([k]) => k === settings.day_of_week)?.[1]}</b> at <b>{String(settings.hour).padStart(2, "0")}:{String(settings.minute).padStart(2, "0")}</b> {settings.timezone}.
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="p-6 border-t border-[var(--border)] flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary" data-testid="pulse-cancel">Close</button>
          <button onClick={saveSchedule} disabled={busy || !settings} className="btn-primary" data-testid="pulse-save">
            {busy ? "Saving…" : "Save schedule"}
          </button>
        </div>
      </div>
    </div>
  );
}



function InterestedServicesCard({ interested, days }) {
  if (!interested) return null;
  const rows = interested.tags || [];
  const maxLeads = Math.max(1, ...rows.map((r) => r.leads));
  return (
    <div className="card p-5" data-testid="interested-services-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="overline">Interested services</div>
          <div className="text-[16px] font-medium">What clients want</div>
        </div>
        <div className="text-[11px] text-[var(--ink-3)]">Last {days}d</div>
      </div>
      {rows.length === 0 ? (
        <div className="text-[var(--ink-3)] text-[13px]">No tagged leads in this window.</div>
      ) : (
        <div className="space-y-2">
          {rows.slice(0, 12).map((r) => (
            <div key={r.tag} className="text-[12px]" data-testid={`interested-row-${r.tag}`}>
              <div className="flex items-center justify-between mb-0.5">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-[var(--ink)]">{r.tag}</span>
                  {r.referrals > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full"
                          style={{ background: "var(--subtle)", color: "var(--ink-2)" }}
                          title="Tagged to teammates">
                      {r.referrals} referral{r.referrals === 1 ? "" : "s"}
                    </span>
                  )}
                </div>
                <div className="text-[var(--ink-3)] font-mono">
                  {r.leads} lead{r.leads === 1 ? "" : "s"} · ${r.total_estimated_value.toLocaleString()} value
                </div>
              </div>
              <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "var(--subtle)" }}>
                <div className="h-full" style={{
                  width: `${(r.leads / maxLeads) * 100}%`,
                  background: "var(--ink)",
                }}/>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function fmtHM(mins) {
  const h = Math.floor((mins || 0) / 60), m = (mins || 0) % 60;
  return h ? `${h}h ${m}m` : `${m}m`;
}

function StaffWorkloadCard({ staff }) {
  const [drillUser, setDrillUser] = useState(null);
  const [members, setMembers] = useState([]);
  useEffect(() => {
    axios.get(`${API_BASE}/team/members`).then((r) => setMembers(r.data || [])).catch(() => {});
  }, []);
  if (!staff) return null;
  const rows = staff.rows || [];
  const maxHeat = Math.max(1, ...rows.map((r) => r.heat_score || 0));
  const maxMinutes = Math.max(1, ...rows.map((r) => r.minutes_this_week || 0));
  return (
    <div className="card p-5" data-testid="staff-workload-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="overline">Staff workload · this week</div>
          <div className="text-[16px] font-medium">Who's swamped, who's free</div>
        </div>
        <div className="text-[11px] text-[var(--ink-3)]">{rows.length} teammates</div>
      </div>
      {rows.length === 0 ? (
        <div className="text-[var(--ink-3)] text-[13px]">No active teammates yet.</div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <button
              key={r.user_id}
              type="button"
              onClick={() => setDrillUser(r)}
              data-testid={`staff-workload-row-${r.user_id}`}
              className="w-full text-left text-[12px] hover:bg-[var(--subtle)] rounded-md -mx-1 px-1 py-1 transition"
            >
              <div className="flex items-center justify-between mb-1.5 gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-medium text-[var(--ink)] truncate">{r.name || r.email}</span>
                  {r.role && r.role !== "owner" && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full uppercase tracking-wide flex-shrink-0"
                          style={{ background: "var(--subtle)", color: "var(--ink-3)" }}>
                      {r.role}
                    </span>
                  )}
                </div>
                <div className="text-[var(--ink-3)] font-mono text-[11px] flex-shrink-0">
                  {r.open_jobs} jobs · {r.open_tasks} tasks · {fmtHM(r.minutes_this_week)}
                </div>
              </div>
              {/* Stacked bar: jobs (dark), tasks (mid), hours (subtle) */}
              <div className="flex items-center gap-0.5 h-2 rounded-full overflow-hidden" style={{ background: "var(--subtle)" }}>
                <div style={{
                  width: `${Math.min(100, (r.open_jobs * 2 / maxHeat) * 100)}%`,
                  background: "var(--ink)",
                }}/>
                <div style={{
                  width: `${Math.min(100, (r.open_tasks / maxHeat) * 100)}%`,
                  background: "var(--accent)",
                }}/>
              </div>
              {r.minutes_this_week > 0 && (
                <div className="h-1 rounded-full mt-1 overflow-hidden" style={{ background: "var(--subtle)" }}>
                  <div style={{
                    width: `${(r.minutes_this_week / maxMinutes) * 100}%`,
                    background: "var(--moss)",
                  }}/>
                </div>
              )}
            </button>
          ))}
          <div className="flex items-center gap-3 text-[10px] text-[var(--ink-3)] mt-2 pt-2 border-t" style={{ borderColor: "var(--border)" }}>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block" style={{ background: "var(--ink)" }}/> Open jobs</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block" style={{ background: "var(--accent)" }}/> Open tasks</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block" style={{ background: "var(--moss)" }}/> Hours this week</span>
          </div>
        </div>
      )}
      <TeammateDrillThrough open={!!drillUser} user={drillUser} members={members} onClose={() => setDrillUser(null)} />
    </div>
  );
}

function PipelineTrendCard({ trend }) {
  if (!trend) return null;
  const series = trend.series || [];
  const forecast = trend.forecast || [];
  const modelLabel = {
    linear_regression: "Forecast (linear trend)",
    trailing_average: "Forecast (trailing avg)",
    none: "Forecast",
  }[trend.forecast_model] || "Forecast";
  const hasSigma = forecast.some((b) => (b.forecast_sigma || 0) > 0);
  const fmtMoneyShort = (v) => v >= 1000 ? `$${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : `$${v.toFixed(0)}`;
  const maxVal = Math.max(1, ...series.map((b) => b.new_pipeline_value || 0),
                             ...forecast.map((b) => b.forecast_value || 0));
  return (
    <div className="card p-5" data-testid="pipeline-trend-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="overline">Pipeline added · {trend.weeks}w + 4w forecast</div>
          <div className="text-[16px] font-medium">
            {fmtMoneyShort(trend.total_value || 0)} <span className="text-[var(--ink-3)] text-[12px] font-normal">total · {trend.total_leads} new leads</span>
          </div>
        </div>
        <div className="text-[11px] text-[var(--ink-3)] font-mono">avg {fmtMoneyShort(trend.avg_value_per_week || 0)}/wk</div>
      </div>
      {series.length === 0 ? (
        <div className="text-[var(--ink-3)] text-[13px]">No data yet.</div>
      ) : (
        <>
          <div className="flex items-end gap-1 h-28">
            {series.map((b) => {
              const pct = ((b.new_pipeline_value || 0) / maxVal) * 100;
              const minH = b.new_leads > 0 ? 4 : 0;
              return (
                <div key={b.week_start} className="flex-1 flex flex-col items-center justify-end h-full group relative" data-testid={`pipeline-trend-week-${b.week_start}`}>
                  <div
                    className="w-full rounded-t transition-opacity"
                    style={{
                      height: `${Math.max(pct, minH)}%`,
                      background: b.new_pipeline_value > 0 ? "var(--ink)" : "var(--subtle)",
                      minHeight: minH ? "4px" : 0,
                    }}
                  />
                  <div className="absolute -top-7 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap text-[10px] px-1.5 py-0.5 rounded shadow"
                       style={{ background: "var(--ink)", color: "var(--paper)" }}>
                    {b.week_start.slice(5)}: {fmtMoneyShort(b.new_pipeline_value || 0)} · {b.new_leads}
                  </div>
                </div>
              );
            })}
            {forecast.map((b) => {
              const pct = ((b.forecast_value || 0) / maxVal) * 100;
              const sigma = b.forecast_sigma || 0;
              const upperPct = Math.min(100, ((b.forecast_value + sigma) / maxVal) * 100);
              const lowerPct = Math.max(0, ((b.forecast_value - sigma) / maxVal) * 100);
              const bandHeight = Math.max(0, upperPct - lowerPct);
              return (
                <div key={`fc-${b.week_start}`} className="flex-1 flex flex-col items-center justify-end h-full group relative" data-testid={`pipeline-forecast-week-${b.week_start}`}>
                  {/* Confidence band — faint shaded zone behind the dashed bar */}
                  {sigma > 0 && (
                    <div
                      className="absolute left-[15%] right-[15%]"
                      style={{
                        bottom: `${lowerPct}%`,
                        height: `${bandHeight}%`,
                        background: "var(--ink-3)",
                        opacity: 0.18,
                        borderRadius: 2,
                        pointerEvents: "none",
                      }}
                    />
                  )}
                  <div
                    className="w-full rounded-t transition-opacity relative z-10"
                    style={{
                      height: `${Math.max(pct, 4)}%`,
                      background: "transparent",
                      border: "1px dashed var(--ink-3)",
                      borderBottom: "none",
                    }}
                  />
                  <div className="absolute -top-7 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap text-[10px] px-1.5 py-0.5 rounded shadow z-20"
                       style={{ background: "var(--ink-2)", color: "var(--paper)" }}>
                    forecast · {b.week_start.slice(5)}: {fmtMoneyShort(b.forecast_value || 0)}
                    {sigma > 0 && <> ±{fmtMoneyShort(sigma)}</>}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-between text-[10px] text-[var(--ink-3)] font-mono mt-2">
            <span>{series[0]?.week_start.slice(5)}</span>
            <span>now</span>
            <span>{forecast.length > 0 ? `+${forecast.length}w` : ""}</span>
          </div>
          <div className="flex items-center gap-3 text-[10px] text-[var(--ink-3)] mt-2 pt-2 border-t" style={{ borderColor: "var(--border)" }}>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 inline-block" style={{ background: "var(--ink)" }}/> Actual</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 inline-block border border-dashed" style={{ borderColor: "var(--ink-3)" }}/> {modelLabel}</span>
            {hasSigma && (
              <span className="inline-flex items-center gap-1" data-testid="pipeline-trend-sigma-legend">
                <span className="w-2 h-2 inline-block rounded-sm" style={{ background: "var(--ink-3)", opacity: 0.18 }}/> ±1σ band
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}


function JobProfitabilityCard() {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API_BASE}/reports/job-profitability?days=90`);
        if (alive) setRows(r.data?.jobs || []);
      } catch (e) {
        console.warn("profitability load failed", e?.message);
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  const top = (rows || []).slice(0, 10);
  const fmt = (n) => `$${Math.round(n || 0).toLocaleString()}`;
  return (
    <div className="card p-6" data-testid="job-profitability-card">
      <div className="overline mb-1 inline-flex items-center gap-1.5">
        <BarChart3 size={11}/> Job profitability
      </div>
      <div className="font-heading text-2xl mb-1">Margin by job</div>
      <div className="text-[12px] text-[var(--ink-3)] mb-4">
        Top 10 jobs ranked by margin (billed minus actual time cost @ staff hourly rate)
      </div>
      {loading ? (
        <div className="text-[12px] text-[var(--ink-3)]">Loading…</div>
      ) : top.length === 0 ? (
        <div className="text-[12px] text-[var(--ink-3)] italic" data-testid="job-profitability-empty">
          No jobs found yet. Margin will appear once you log time and send invoices.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-[var(--ink-3)] border-b border-[var(--border)]">
                <th className="text-left py-2">Job</th>
                <th className="text-right py-2">Hours</th>
                <th className="text-right py-2">Cost</th>
                <th className="text-right py-2">Billed</th>
                <th className="text-right py-2">Margin</th>
                <th className="text-right py-2">%</th>
              </tr>
            </thead>
            <tbody>
              {top.map((j) => (
                <tr key={j.job_id} className="border-b border-[var(--border)] last:border-0"
                    data-testid={`job-profitability-row-${j.job_id}`}>
                  <td className="py-2 truncate max-w-[200px]" title={j.title}>{j.title}</td>
                  <td className="py-2 text-right font-mono text-[var(--ink-3)]">
                    {(j.total_minutes / 60).toFixed(1)}h
                  </td>
                  <td className="py-2 text-right font-mono">{fmt(j.actual_cost)}</td>
                  <td className="py-2 text-right font-mono">{fmt(j.billed)}</td>
                  <td className="py-2 text-right font-mono font-medium"
                      style={{ color: j.margin >= 0 ? "var(--moss)" : "#B91C1C" }}>
                    {j.margin < 0 ? "-" : ""}{fmt(Math.abs(j.margin))}
                  </td>
                  <td className="py-2 text-right font-mono text-[var(--ink-3)]">
                    {j.margin_pct ? `${j.margin_pct}%` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// v68.11 — Realisation report: billed vs cost grouped by stage_role.
// Uses the rate resolver as its cost engine.
// v68.12 — Adds a 12-week sparkline of firm realisation %.
function RealisationCard({ data, trend, days }) {
  if (!data) return null;
  const fmt$ = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  const firm = data.firm || {};
  const rows = data.rows || [];
  const empty = firm.cost === 0 && firm.billed === 0;

  return (
    <div className="module-card lg:col-span-2" data-testid="realisation-card">
      <div className="flex items-start justify-between mb-4 gap-3">
        <div>
          <div className="overline">Realisation</div>
          <h3 className="font-heading text-xl mt-1" style={{ fontWeight: 500 }}>
            Billed vs cost · last {days} days
          </h3>
          <p className="text-[12px] text-[var(--ink-2)] mt-1 max-w-xl">
            Every billable time entry costed through the rate resolver (rate-plan role rate,
            or user hourly-rate fallback), then compared to what actually landed on paid invoices.
          </p>
          <OverdueDigestButton />
        </div>
        <div className="text-right">
          <div className="overline text-[9px]">Firm realisation</div>
          <div className="font-heading text-3xl leading-none" style={{ fontWeight: 500 }}>
            {firm.realisation_pct == null ? "—" : `${firm.realisation_pct}%`}
          </div>
          <RealisationSparkline trend={trend} />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="border border-[var(--border)] rounded-md p-3 bg-white">
          <div className="overline text-[9px] text-[var(--ink-3)]">Cost</div>
          <div className="font-mono text-lg mt-0.5">{fmt$(firm.cost)}</div>
        </div>
        <div className="border border-[var(--border)] rounded-md p-3 bg-white">
          <div className="overline text-[9px] text-[var(--ink-3)]">Billed (sent + paid)</div>
          <div className="font-mono text-lg mt-0.5">{fmt$(firm.billed)}</div>
        </div>
        <div className="border border-[var(--border)] rounded-md p-3 bg-white">
          <div className="overline text-[9px] text-[var(--ink-3)]">Unbilled WIP</div>
          <div className="font-mono text-lg mt-0.5" style={{ color: (firm.unbilled_cost > 0) ? "var(--accent)" : "inherit" }}>
            {fmt$(firm.unbilled_cost)}
          </div>
        </div>
      </div>

      {empty ? (
        <div className="text-center text-[13px] text-[var(--ink-3)] py-6 border border-dashed border-[var(--border)] rounded-md">
          No costed hours in the window yet.
          <div className="text-[11px] mt-1">
            Assign <em>stage roles</em> on the Team page and log time to see realisation figures.
          </div>
        </div>
      ) : (
        <table className="w-full text-[13px]" data-testid="realisation-table">
          <thead>
            <tr className="text-left text-[var(--ink-3)] border-b border-[var(--border)]">
              <th className="py-1.5 font-normal overline text-[9px]">Role</th>
              <th className="py-1.5 font-normal overline text-[9px] text-right">Hours</th>
              <th className="py-1.5 font-normal overline text-[9px] text-right">Cost</th>
              <th className="py-1.5 font-normal overline text-[9px] text-right">Billed</th>
              <th className="py-1.5 font-normal overline text-[9px] text-right">Realisation</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.role} className="border-b border-[var(--border)]"
                  data-testid={`realisation-row-${r.role}`}>
                <td className="py-2 font-medium">{r.role.replace(/_/g," ")}</td>
                <td className="py-2 text-right font-mono">{r.hours}</td>
                <td className="py-2 text-right font-mono">{fmt$(r.cost)}</td>
                <td className="py-2 text-right font-mono">{fmt$(r.billed)}</td>
                <td className="py-2 text-right font-mono">
                  {r.realisation_pct == null ? "—" : (
                    <span style={{ color: r.realisation_pct >= 90
                                          ? "var(--moss)"
                                          : r.realisation_pct >= 60
                                              ? "inherit"
                                              : "var(--accent)" }}>
                      {r.realisation_pct}%
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}


// v68.12 — Tiny SVG sparkline of realisation % across N weekly buckets.
// Renders inline in the RealisationCard header. Nulls (weeks with no cost)
// break the line so we don't imply data we don't have.
function RealisationSparkline({ trend }) {
  const [drillOpen, setDrillOpen] = useState(null);   // {week_start} being drilled
  if (!trend?.buckets?.length) return null;
  const buckets = trend.buckets;
  const w = 140, h = 32;
  const values = buckets.map((b) => b.realisation_pct);
  const filled = values.filter((v) => v != null);
  if (filled.length < 2) {
    return (
      <div className="text-[10px] text-[var(--ink-3)] mt-1" data-testid="realisation-sparkline-empty">
        Not enough weekly data yet
      </div>
    );
  }
  const min = Math.min(...filled, 60);
  const max = Math.max(...filled, 100);
  const range = Math.max(1, max - min);
  const toXY = (val, i) => {
    const x = (i / (buckets.length - 1)) * w;
    const y = h - ((val - min) / range) * h;
    return [x, y];
  };
  const segments = [];
  let cur = [];
  values.forEach((v, i) => {
    if (v == null) { if (cur.length) segments.push(cur); cur = []; }
    else { cur.push([...toXY(v, i), i]); }
  });
  if (cur.length) segments.push(cur);
  const last = filled[filled.length - 1];
  const first = filled[0];
  const trending = last - first;
  const arrow = trending > 1.5 ? "▲" : trending < -1.5 ? "▼" : "→";
  const arrowColor = trending > 1.5 ? "var(--moss)"
                     : trending < -1.5 ? "var(--accent)" : "var(--ink-3)";

  return (
    <>
      <div className="mt-1 flex items-center gap-1.5 justify-end" data-testid="realisation-sparkline">
        <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}
             className="overflow-visible" style={{ opacity: 0.85 }}>
          {segments.map((seg, i) => (
            <polyline key={i} fill="none" stroke="var(--ink)" strokeWidth="1.5"
              strokeLinejoin="round" strokeLinecap="round"
              points={seg.map(([x, y]) => `${x},${y}`).join(" ")} />
          ))}
          {/* Clickable dots for each data point → open drill modal */}
          {segments.flatMap((seg) =>
            seg.map(([x, y, idx]) => (
              <circle key={`dot-${idx}`} cx={x} cy={y} r={idx === buckets.length - 1 ? 3 : 2}
                fill={idx === buckets.length - 1 ? "var(--accent)" : "var(--ink)"}
                style={{ cursor: "pointer" }}
                onClick={() => setDrillOpen(buckets[idx])}
                data-testid={`realisation-sparkline-dot-${idx}`}>
                <title>Week of {buckets[idx].week_start}: {buckets[idx].realisation_pct ?? "—"}%</title>
              </circle>
            ))
          )}
        </svg>
        <span className="text-[10px] font-mono" style={{ color: arrowColor }}
              data-testid="realisation-sparkline-arrow">
          {arrow}{trending > 0 ? "+" : ""}{Math.round(trending)}pp
        </span>
      </div>
      {drillOpen && <TrendDrillModal bucket={drillOpen} onClose={() => setDrillOpen(null)} />}
    </>
  );
}

// v68.13 — Drill into a single week's entries + invoices from the sparkline.
function TrendDrillModal({ bucket, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_BASE}/reports/realisation-trend/week?start=${bucket.week_start}`);
        setData(r.data);
      } catch (err) { toast.error(formatApiError(err)); onClose(); }
      finally { setLoading(false); }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bucket.week_start]);

  const fmt$ = (n) => `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  return (
    <div className="fixed inset-0 z-[70] bg-[var(--ink)]/50 flex items-center justify-center p-6"
         onClick={onClose} data-testid="realisation-drill-modal">
      <div className="bg-[var(--paper)] rounded-md border border-[var(--border)] p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="overline">Realisation · week drilldown</div>
            <h3 className="font-heading text-xl mt-1" style={{ fontWeight: 500 }}>
              Week of {bucket.week_start}
            </h3>
            <p className="text-[11px] text-[var(--ink-3)] mt-1">
              Cost {fmt$(bucket.cost)} · Billed {fmt$(bucket.billed)} · Realisation {bucket.realisation_pct ?? "—"}%
            </p>
          </div>
          <button onClick={onClose} className="text-[var(--ink-3)] hover:text-[var(--ink)]">×</button>
        </div>
        {loading && <div className="text-[13px] text-[var(--ink-3)]">Loading…</div>}
        {data && (
          <>
            <div className="overline text-[10px] mb-1.5">Time entries ({data.entries.length})</div>
            {data.entries.length === 0 ? (
              <div className="text-[12px] text-[var(--ink-3)] py-2">No time entries logged this week.</div>
            ) : (
              <table className="w-full text-[12px] mb-4" data-testid="realisation-drill-entries">
                <thead>
                  <tr className="text-left text-[var(--ink-3)] border-b border-[var(--border)]">
                    <th className="py-1 font-normal">Staff</th>
                    <th className="py-1 font-normal text-right">Hours</th>
                    <th className="py-1 font-normal text-right">Rate</th>
                    <th className="py-1 font-normal text-right">Cost</th>
                    <th className="py-1 font-normal">Billed?</th>
                  </tr>
                </thead>
                <tbody>
                  {data.entries.slice(0, 30).map((e) => (
                    <tr key={e.time_entry_id} className="border-b border-[var(--border)]">
                      <td className="py-1 truncate max-w-[180px]">{e.user_name}</td>
                      <td className="py-1 text-right font-mono">{e.hours}</td>
                      <td className="py-1 text-right font-mono">${e.rate}</td>
                      <td className="py-1 text-right font-mono">{fmt$(e.cost)}</td>
                      <td className="py-1 text-[10px]">{e.invoice_id ? "✓" : (e.billable ? "WIP" : "n/b")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div className="overline text-[10px] mb-1.5">Invoices ({data.invoices.length})</div>
            {data.invoices.length === 0 ? (
              <div className="text-[12px] text-[var(--ink-3)] py-2">No invoices touch these entries.</div>
            ) : (
              <div className="space-y-1" data-testid="realisation-drill-invoices">
                {data.invoices.map((i) => (
                  <div key={i.invoice_id} className="flex items-center gap-2 text-[12px] border border-[var(--border)] rounded px-2 py-1">
                    <span className="font-mono">{i.number || i.invoice_id.slice(0, 12)}</span>
                    <span className="overline text-[9px]">{i.status}</span>
                    <span className="flex-1"/>
                    <span className="font-mono">{fmt$(i.total)}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}


// v68.12 — Manual "Send weekly overdue digest now" button.
// Ships with a preview drawer so you see who'd get an email before sending.
function OverdueDigestButton() {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadPreview = async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API_BASE}/reports/overdue-digest/preview`);
      setPreview(data);
      setPreviewOpen(true);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const send = async () => {
    if (!window.confirm(
      `Send the overdue digest to ${preview?.recipients?.filter(r => !r.already_sent_this_week).length || 0} teammate(s) right now? ` +
      `(Already-sent users this week are skipped.)`
    )) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_BASE}/reports/overdue-digest/run-now`);
      toast.success(`Sent ${data.sent} · skipped ${data.skipped}`);
      setPreviewOpen(false); setPreview(null);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={loadPreview} disabled={busy}
              className="mt-2 text-[11px] text-[var(--ink-2)] hover:text-[var(--ink)] inline-flex items-center gap-1 underline decoration-dotted underline-offset-2"
              data-testid="overdue-digest-btn">
        {busy ? "Loading…" : "Send weekly overdue digest now →"}
      </button>
      {previewOpen && preview && (
        <div className="fixed inset-0 z-[70] bg-[var(--ink)]/50 flex items-center justify-center p-6"
             onClick={() => setPreviewOpen(false)} data-testid="overdue-digest-preview">
          <div className="bg-[var(--paper)] rounded-md border border-[var(--border)] p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="overline">Overdue digest · preview</div>
                <h3 className="font-heading text-xl mt-1" style={{ fontWeight: 500 }}>
                  {preview.total_overdue} overdue job{preview.total_overdue === 1 ? "" : "s"} across {preview.recipients.length} teammate{preview.recipients.length === 1 ? "" : "s"}
                </h3>
                <p className="text-[11px] text-[var(--ink-3)] mt-1">
                  Week {preview.week_key} — users already emailed this week are skipped.
                </p>
              </div>
              <button onClick={() => setPreviewOpen(false)} className="text-[var(--ink-3)] hover:text-[var(--ink)]">
                ×
              </button>
            </div>
            <div className="space-y-2 mb-4">
              {preview.recipients.map((r) => (
                <div key={r.user_id}
                     className={`border border-[var(--border)] rounded p-3 ${r.already_sent_this_week ? "opacity-50" : ""}`}
                     data-testid={`overdue-recipient-${r.user_id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium text-[13px] truncate">{r.name}</div>
                      <div className="text-[10px] text-[var(--ink-3)] font-mono truncate">{r.email}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-mono text-[15px]">{r.overdue_count}</div>
                      {r.already_sent_this_week && (
                        <div className="text-[9px] text-[var(--ink-3)]">already sent</div>
                      )}
                    </div>
                  </div>
                  {r.sample_titles?.length > 0 && (
                    <div className="text-[10px] text-[var(--ink-3)] mt-1 italic truncate">
                      e.g. {r.sample_titles.join(" · ")}
                    </div>
                  )}
                </div>
              ))}
              {preview.recipients.length === 0 && (
                <div className="text-center text-[13px] text-[var(--ink-3)] py-8">
                  Nothing to send — no overdue jobs assigned to anyone.
                </div>
              )}
            </div>
            <div className="flex gap-2 pt-3 border-t border-[var(--border)]">
              <button className="btn-secondary flex-1 justify-center" onClick={() => setPreviewOpen(false)}>
                Cancel
              </button>
              <button className="btn-primary flex-1 justify-center" onClick={send}
                      disabled={busy || preview.recipients.length === 0}
                      data-testid="overdue-digest-send">
                Send now
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}



// v68.38 — Referral revenue card. Aggregates converted expected_fee by
// partner / type / month over the current dashboard `days` window.
function ReferralRevenueCard({ days }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    axios.get(`${API_BASE}/referrals/reports/summary?days=${days || 90}`)
      .then((r) => { if (live) setData(r.data); })
      .catch(() => { if (live) setData(null); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [days]);

  return (
    <div className="card p-5" data-testid="reports-referral-revenue">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="overline mb-1 inline-flex items-center gap-1">
            <Share2 size={11}/> Referral revenue
          </div>
          <h3 className="font-heading text-xl" style={{ fontWeight: 500 }}>
            Outgoing referrals · P&L
          </h3>
          <p className="text-[12px] text-[var(--ink-3)] mt-1">
            Converted expected fees over the last {days || 90} days.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="text-[13px] text-[var(--ink-3)]">Loading…</div>
      ) : !data || data.totals.assignments === 0 ? (
        <div className="text-[13px] text-[var(--ink-3)] italic"
             data-testid="reports-referral-empty">
          No referrals recorded in this window.
        </div>
      ) : (
        <div className="space-y-4">
          {/* Totals row */}
          <div className="grid grid-cols-3 gap-2 text-center">
            <MiniStat label="Assignments" value={data.totals.assignments} />
            <MiniStat label="Converted"   value={data.totals.converted} tone="moss" />
            <MiniStat label="Fees earned" value={formatMoney(data.totals.expected_fee_converted)} tone="moss" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Top partners */}
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[var(--ink-3)] mb-1.5">
                Top partners
              </div>
              <table className="w-full text-[12px]" data-testid="reports-referral-partners">
                <thead>
                  <tr className="text-[10px] uppercase text-[var(--ink-3)]">
                    <th className="text-left font-medium py-1">Partner</th>
                    <th className="text-left font-medium py-1">Type</th>
                    <th className="text-right font-medium py-1">Conv</th>
                    <th className="text-right font-medium py-1">Fees</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_partner.slice(0, 6).map((p) => (
                    <tr key={p.partner_id} className="border-t border-[var(--border)]">
                      <td className="py-1">{p.partner_name || "—"}</td>
                      <td className="py-1 text-[var(--ink-3)]">{p.type_name || "—"}</td>
                      <td className="py-1 text-right">{p.converted} / {p.total}</td>
                      <td className="py-1 text-right font-medium">
                        {formatMoney(p.expected_fee_converted)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* By type */}
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[var(--ink-3)] mb-1.5">
                By partner type
              </div>
              <table className="w-full text-[12px]" data-testid="reports-referral-types">
                <thead>
                  <tr className="text-[10px] uppercase text-[var(--ink-3)]">
                    <th className="text-left font-medium py-1">Type</th>
                    <th className="text-right font-medium py-1">Conv</th>
                    <th className="text-right font-medium py-1">Fees</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_type.map((t) => (
                    <tr key={t.type_id || "n"} className="border-t border-[var(--border)]">
                      <td className="py-1">{t.type_name || "—"}</td>
                      <td className="py-1 text-right">{t.converted} / {t.total}</td>
                      <td className="py-1 text-right font-medium">
                        {formatMoney(t.expected_fee_converted)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* By month sparkline */}
          {data.by_month.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[var(--ink-3)] mb-1.5">
                Monthly conversions
              </div>
              <div className="flex items-end gap-1 h-16" data-testid="reports-referral-monthly">
                {data.by_month.map((m) => {
                  const max = Math.max(1, ...data.by_month.map((x) => x.expected_fee_converted || 0));
                  const h = Math.round(((m.expected_fee_converted || 0) / max) * 100);
                  return (
                    <div key={m.month} className="flex-1 flex flex-col items-center gap-0.5">
                      <div className="w-full rounded-t bg-[var(--moss)]/70 relative"
                           style={{ height: `${Math.max(h, 2)}%` }}
                           title={`${m.month} · ${m.converted} converted · ${formatMoney(m.expected_fee_converted)}`} />
                      <div className="text-[9px] text-[var(--ink-3)] font-mono">
                        {m.month.slice(5)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function MiniStat({ label, value, tone }) {
  const bg = tone === "moss" ? "bg-[var(--moss)]/8" : "bg-white";
  return (
    <div className={`border border-[var(--border)] rounded p-2 ${bg}`}>
      <div className="text-[10px] uppercase tracking-wider text-[var(--ink-3)]">{label}</div>
      <div className="font-heading text-lg" style={{ fontWeight: 500 }}>{value}</div>
    </div>
  );
}

